/**
 * T084 — Tenant + role hook.
 *
 * Thin wrapper around `GET /api/v1/session`. Cached for 30 s.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export interface SessionInfo {
  user: {
    id: string;
    email: string;
    name: string | null;
    globalRole: 'admin' | 'geschäftsführer' | 'steuerberater' | null;
  };
  activeTenantId: string | null;
  activeRole: 'admin' | 'geschäftsführer' | 'steuerberater' | null;
  availableTenants: Array<{
    tenantId: string;
    slug: string;
    name: string;
    role: 'admin' | 'geschäftsführer' | 'steuerberater';
    isPrimary: boolean;
  }>;
}

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<SessionInfo>('/session'),
    staleTime: 30_000,
  });
}

export function useSelectTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      api.post<{ activeTenantId: string; activeRole: string }>(
        '/session/select-tenant',
        { tenantId }
      ),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/**
 * Sprint 006 (B1) — active tenant id for tenant-scoped query keys.
 * `null` while the session is loading / no tenant selected; queries
 * should gate on it (`enabled: tenantId !== null`).
 */
export function useActiveTenantId(): string | null {
  const { data } = useSession();
  return data?.activeTenantId ?? null;
}

export function useRoleGuard(allowed: Array<'admin' | 'geschäftsführer' | 'steuerberater'>) {
  const { data } = useSession();
  if (!data) return { allowed: null as boolean | null };
  return { allowed: data.activeRole ? allowed.includes(data.activeRole) : false };
}
